/**
 * WorkflowGeneralTab - editable label + description, plus read-only
 * display of the immutable fields (key, resource_type, version).
 *
 * Saves via `useUpdateWorkflow` which PATCHes the parent resource
 * directly; key/resource_type live as read-only badges at the top of
 * the detail shell.
 */

import { useMemo } from 'react'
import { toast } from 'sonner'

import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { Button } from '@/shared/components/ui/button'
import {
  useUpdateWorkflow,
  type WorkflowDefinitionAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { AttributesSection } from './attributes-section'

interface WorkflowGeneralTabProps {
  workflow: WorkflowDefinitionAdmin
}

export function WorkflowGeneralTab({ workflow }: WorkflowGeneralTabProps) {
  const update = useUpdateWorkflow()

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'label',
        label: 'Label',
        value: workflow.label,
        required: 'Label is required',
      },
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Describe what this workflow governs.',
        value: workflow.description,
      },
    ],
    [workflow.label, workflow.description],
  )

  const { Form, ref } = useForm(
    {
      name: `workflow-general-${workflow.uuid}`,
      submit: 'footer',
      fields,
    },
    async (rawValues) => {
      const values = rawValues as { label: string; description: string }
      try {
        await update.mutateAsync({
          uuid: workflow.uuid,
          label: values.label.trim(),
          description: (values.description ?? '').trim(),
        })
        toast.success('Workflow updated')
      } catch (err) {
        toastApiError(err, 'Failed to update workflow')
      }
    },
    [workflow.uuid, fields],
  )

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <Form className="flex flex-col gap-4" />
        <div className="flex justify-end">
          <Button
            onClick={() => ref.current?.submitForm()}
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </section>

      <AttributesSection workflowUuid={workflow.uuid} />
    </div>
  )
}
